import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiClientWrapper, createApiClientWrapper } from '@spike/api-client';
import { showUnauthorizedThunk } from '@spike/notifications-action';
import { RootState, dispatch } from 'store';
import { v4 as uuid } from 'uuid';
import { PaymentLink } from 'actions/payByLink/PayByLinkDto';
import MasterData from '@spike/masterdata-model';
import { getSelectedSettingsMarketplaceId } from './settingsMarketplaceSelection';

let abortController = new AbortController();

const sessionUuidKey = 'sessionUuid';

const getSession = (): string => {
    const sessionUuid = sessionStorage.getItem(sessionUuidKey);
    const windowName = window.name;

    if (sessionUuid && windowName.length > 0) {
        return sessionUuid;
    } else {
        const newSessionUuid = uuid();

        sessionStorage.setItem(sessionUuidKey, newSessionUuid);

        //To detect a new Tab created by duplication, because the sessionStorage
        //is copied but not the window.name.
        window.name = newSessionUuid;

        return newSessionUuid;
    }
};

export const sessionUuid = getSession();

const config: AxiosRequestConfig = {
    baseURL: `${process.env.REACT_APP_HOST_URL}${process.env.REACT_APP_API_BASE_URL}`,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Session-Uuid': sessionUuid,
        'Channel': 'marketplace'
    },
    timeout: 60000
};

const ApiClient: AxiosInstance = axios.create(config);
const unauthorizedRoleMessage =
    'Your role is not authorized to perform this action. Please ask your manager for authorization.';

ApiClient.interceptors.response.use(
    response => {
        const headerVersion = response.headers[process.env.REACT_APP_VERSION_HEADER_NAME || 'version'];

        if (headerVersion !== undefined && headerVersion !== process.env.REACT_APP_VERSION) {
            window.location.reload();
        }

        // TODO(SU-2171): TEMPORARY PATCH — DO NOT SHIP. The backend now returns
        // master_data.pet_requirements keyed by species ({dogs, cats}), but
        // @spike/masterdata-action and @spike/marketplace-action still expect the
        // flat array, which crashes convertMarketplace and hangs the app on the
        // spinner. Normalize back to the legacy shape (dogs) until the lib
        // counterpart of SU-2171 ships, then remove this block.
        if (
            response.config?.url?.includes('master_data') &&
            response.data?.pet_requirements &&
            !Array.isArray(response.data.pet_requirements)
        ) {
            response.data.pet_requirements = response.data.pet_requirements.dogs ?? [];
        }

        return response;
    },
    error => {
        const requestUrl: string = error?.config?.url?.toLowerCase() || '';
        const isLoginRequest = requestUrl.includes('login');

        if (error?.response?.status === 401 && !isLoginRequest) {
            dispatch(showUnauthorizedThunk(unauthorizedRoleMessage));
        }

        return Promise.reject(error);
    }
);

export const createWrapper = (state: RootState): ApiClientWrapper => {
    const authMarketplaceId = state.login.auth.marketplaceId!;
    const marketplace = state.marketplace.marketplace;

    return createApiClientWrapper(
        ApiClient,
        createTokenConfig(state.login.auth.token!),
        createMultipartTokenConfig(state.login.auth.token!),
        authMarketplaceId,
        state.login.auth.timeZone!,
        state.masterData.data,
        uuid,
        state.marketplace.organizationMarketplaces,
        authMarketplaceId === marketplace?.id ? marketplace : undefined
    );
};

export const createSettingsWrapper = (state: RootState): ApiClientWrapper => {
    const authMarketplaceId = state.login.auth.marketplaceId!;
    const authTimeZone = state.login.auth.timeZone!;
    const settingsMarketplace = state.marketplace.settingsMarketplace;
    const settingsMarketplaceId = settingsMarketplace?.id;
    const selectedMarketplaceId = getSelectedSettingsMarketplaceId();
    const hasSettingsMarketplace = typeof settingsMarketplaceId === 'number';
    const hasSelectedMarketplace = typeof selectedMarketplaceId === 'number';
    // The location combo is the source of truth. settingsMarketplace updates only after its fetch.
    const marketplaceId = hasSelectedMarketplace
        ? selectedMarketplaceId
        : hasSettingsMarketplace
        ? settingsMarketplaceId
        : authMarketplaceId;
    const settingsMarketplaceMatches = hasSettingsMarketplace && settingsMarketplaceId === marketplaceId;
    const organizationMarketplace = state.marketplace.organizationMarketplaces.find(
        marketplace => marketplace.id === marketplaceId
    );
    const timeZone =
        (settingsMarketplaceMatches ? settingsMarketplace.schedule?.timeZone : undefined) ||
        organizationMarketplace?.timeZone ||
        authTimeZone;

    return createApiClientWrapper(
        ApiClient,
        createTokenConfig(state.login.auth.token!),
        createMultipartTokenConfig(state.login.auth.token!),
        marketplaceId,
        timeZone,
        state.masterData.data,
        uuid,
        state.marketplace.organizationMarketplaces,
        settingsMarketplaceMatches ? settingsMarketplace : undefined
    );
};

export const createWrapperForPayByLink = (paymentLink: PaymentLink, masterData: MasterData): ApiClientWrapper => {
    const authMarketplaceId = paymentLink.marketplace.id!;
    const timeZone = paymentLink.marketplace.calendars.filter(c => c.active)[0].time_zone;

    return createApiClientWrapper(
        ApiClient,
        createTokenConfig(paymentLink.token),
        createMultipartTokenConfig(paymentLink.token),
        authMarketplaceId,
        timeZone,
        masterData,
        uuid,
        [],
        undefined
    );
};

export const createTokenConfig = (token: string): AxiosRequestConfig => {
    if (token) {
        return {
            headers: {
                Authorization: `Bearer ${token}`
            },
            signal: abortController.signal
        };
    } else {
        return {};
    }
};

export const createMultipartTokenConfig = (token: string): AxiosRequestConfig => {
    const tokenConfig = createTokenConfig(token);
    return {
        ...tokenConfig,
        headers: {
            ...tokenConfig.headers,
            'Content-Type': 'multipart/mixed'
        },
        signal: abortController.signal
    };
};

export const cancelAllRequests = () => {
    abortController.abort();
    abortController = new AbortController();
};

export const isCancelled = (error: any) => {
    return axios.isCancel(error);
};

export default ApiClient;
